import { createConsumer } from '@rails/actioncable';

const RECONNECT_INTERVAL = 1000;

// Heartbeat interval; keeps presence timestamp fresh in Redis.
// Must be shorter than the server-side PRESENCE_DURATION (default 600s).
// Browsers throttle setInterval in background tabs (~60s in Chrome) but
// that is still well within PRESENCE_DURATION so the agent stays online.
const PRESENCE_HEARTBEAT_INTERVAL = 30 * 1000;

/**
 * Presence is time-based (TTL in Redis via PRESENCE_DURATION on server).
 * A heartbeat fires every PRESENCE_HEARTBEAT_INTERVAL regardless of tab visibility.
 * The inactivity timer (useInactivityTimer) handles the real logout contract.
 * Tab close triggers unsubscribed → immediate offline via clear_presence_on_disconnect.
 */
class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(app, pubsubToken, websocketHost = '') {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.presenceHeartbeatTimer = null;
    this.isAValidEvent = () => true;

    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.startPresenceHeartbeat();
  }

  startPresenceHeartbeat() {
    this.stopPresenceHeartbeat();
    this.subscription.perform('update_presence');
    this.presenceHeartbeatTimer = setInterval(() => {
      this.subscription.perform('update_presence');
    }, PRESENCE_HEARTBEAT_INTERVAL);
  }

  stopPresenceHeartbeat() {
    if (this.presenceHeartbeatTimer) {
      clearInterval(this.presenceHeartbeatTimer);
      this.presenceHeartbeatTimer = null;
    }
  }

  handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.startPresenceHeartbeat();
    }
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.stopPresenceHeartbeat();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
